import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { EmployeeTimelineFormRule, EmployeeTimelineFormSchema } from "@core/form/form.rule";
import { useFormDefaultEmployeeTimeline } from "@core/form/form-default-employee-timeline.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateEmployeeTimeline, 
  useQyGetEmployeeTimelineById, 
  useQyDeleteEmployeeTimeline 
} from "@core/query/employee-timeline.query";
import { GetResponseEmployeeTimelineModel } from "@core/model/query-model-employee-timeline";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditEmployeeTimeline() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultEmployeeTimeline();
  const navigate = useNavigate();
  const { mapUpdateEmployeeTimeline, mapDeleteEmployeeTimeline } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { employeeTimelineId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID EmployeeTimeline
  const handleGetApiSuccess = (data: GetResponseEmployeeTimelineModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("userCode", responseData?.user_code || "");
      setValue("bucketCode", responseData?.bucket_code || "");
      setValue("source", responseData?.source || "");
      setValue("action", responseData?.action || "");
      setValue("message", responseData?.message || "");
    }
  };
  const {
    data: employeeTimelineResponse,
    isLoading,
    isError: employeeTimelineError,
  } = useQyGetEmployeeTimelineById(employeeTimelineId || "", handleGetApiSuccess);
  const employeeTimelineData = employeeTimelineResponse?.data?.[0];

  // API UPDATE EmployeeTimeline
  const handleUpdateApiSuccess = () => {
    showSuccess("EmployeeTimeline updated successfully");
  };
  const { mutate: updateEmployeeTimeline, isLoading: isEditLoading } = useQyUpdateEmployeeTimeline(
    handleUpdateApiSuccess
  );

  // API DELETE EmployeeTimeline
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("EmployeeTimeline deleted successfully");
  };
  const { mutate: deleteEmployeeTimeline } = useQyDeleteEmployeeTimeline(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<EmployeeTimelineFormSchema>({
    defaultValues: getDefault(employeeTimelineData),
    resolver: zodResolver(EmployeeTimelineFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: EmployeeTimelineFormSchema) => {
    if (!employeeTimelineData) {
      throw new Error("No employeeTimeline data");
    }

    const formData = mapUpdateEmployeeTimeline(form, employeeTimelineData.code);
    updateEmployeeTimeline(formData);
  };
  const handleValidateError = (err: FieldErrors<EmployeeTimelineFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!employeeTimelineData) {
      throw new Error("No employeeTimeline data found");
    }

    const form = mapDeleteEmployeeTimeline(employeeTimelineData.code);
    deleteEmployeeTimeline(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    employeeTimelineId,
    employeeTimelineData,
    isLoading,
    employeeTimelineError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}